/*
 * <home-actions> (sections/home-actions.liquid)
 * Cards with a data-panel open that panel below them instead of following their link; the cards
 * shrink to a row of tabs so the visitor can switch paths. The "upgrade" panel is a three-step
 * flow (Handheld / Home console -> console -> part type) drawn from the sidebar menu JSON
 * (snippets/home-funnel-menu.liquid, fetched when needed from sections/hhl-funnel-menu.liquid), so each final choice
 * is a sidebar link. In the "builders"
 * and "gamepads" panels, a card with [data-detail-open] opens a larger view ([data-detail]) in place
 * of the card grid; previous-build photos enlarge in a dialog.
 *
 * Motion (owner 2026-10-24: one motion language, nothing pops): every change is the same shape. What is leaving fades
 * and sinks 8px; the room (the cards, the panel) eases from its old height to the new one; what arrives rises 8px into
 * place once the room has started to open. Closing is the same played backwards. Durations are the theme's motion
 * tokens (--hhl-dur-fast / --hhl-dur / --hhl-dur-slow, scaled by the "Motion" setting through HHL.motion()); one
 * change runs at a time, and a change asked for mid-way fast-forwards the one running. Reduced motion: instant.
 */
(() => {
  if (customElements.get('home-actions')) return;

  const splitWords = (value) => (value || '').toLowerCase().split(',').map((word) => word.trim()).filter(Boolean);

  /* "Game Boy Color!" -> " game boy color " (spaces around, so words only match whole) */
  const norm = (text) => ` ${(text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;

  /* A title containing any of the words, as whole words */
  const matchesAny = (title, words) => words.some((word) => norm(title).includes(norm(word)));

  const isShopAll = (title) => /^\s*(shop|show|see)\s+all\b/i.test(title);

  /* The words every part link of a console starts with ("GBC Displays", "GBC Power" -> "gbc"),
     used to label the cards ("Displays") and to match builders that use the short name. */
  const sharedPrefix = (links) => {
    const titles = links.map((link) => link.title).filter((title) => !isShopAll(title)).map((title) => norm(title).trim().split(' '));
    if (titles.length < 2) return '';
    const prefix = [];
    for (let i = 0; i < titles[0].length - 1; i++) {
      const word = titles[0][i];
      if (titles.every((words) => words.length > i + 1 && words[i] === word)) prefix.push(word);
      else break;
    }
    return prefix.join(' ');
  };

  const partLabel = (title, prefix) => {
    if (!prefix) return title;
    const words = title.trim().split(/\s+/);
    const count = prefix.split(' ').length;
    if (norm(words.slice(0, count).join(' ')).trim() !== prefix) return title;
    const rest = words.slice(count).join(' ');
    return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : title;
  };

  /* A plain symbol for a part type, picked by words in its name, shown in the photo frame until its product photo has
     loaded (or when it has none). Only symbols true of any console: no shell drawing (it was a Game Boy, shown for
     GameCube shells too) and no stand-in box; with no match the frame stays plain (owner 2026-10-24: real photos or
     a neutral frame, never a wrong console). From Material Design Icons (Pictogrammers, Apache License 2.0). */
  const ICONS = [
    [/display|screen|ips|lcd|oled|backlight/, 'M21,16H3V4H21M21,2H3C1.89,2 1,2.89 1,4V16A2,2 0 0,0 3,18H10V20H8V22H16V20H14V18H21A2,2 0 0,0 23,16V4C23,2.89 22.1,2 21,2Z'],
    [/lens|glass/, 'M8.29 10.28L11.53 7.03L12.59 8.09L9.35 11.34L8.29 10.28M8.7 14.61L14.36 8.95L15.42 10L9.76 15.67L8.7 14.61M18 3V21H6V3H18M20 1H4V23H20V1Z'],
    [/power|battery|charg|usb|cable/, 'M23.05,11H20.05V4L15.05,14H18.05V22M12,20H4L4.05,6H12.05M12.72,4H11.05V2H5.05V4H3.38A1.33,1.33 0 0,0 2.05,5.33V20.67C2.05,21.4 2.65,22 3.38,22H12.72C13.45,22 14.05,21.4 14.05,20.67V5.33A1.33,1.33 0 0,0 12.72,4Z'],
    [/audio|speaker|sound|amp/, 'M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z'],
    [/button|d ?pad|membrane|pad/, 'M4,8H8V4A2,2 0 0,1 10,2H14A2,2 0 0,1 16,4V8H20A2,2 0 0,1 22,10V14A2,2 0 0,1 20,16H16V20A2,2 0 0,1 14,22H10A2,2 0 0,1 8,20V16H4A2,2 0 0,1 2,14V10A2,2 0 0,1 4,8M4,10V14H10V20H14V14H20V10H14V4H10V10H4Z'],
    [/label|sticker|decal/, 'M21.41 11.58L12.41 2.58A2 2 0 0 0 11 2H4A2 2 0 0 0 2 4V11A2 2 0 0 0 2.59 12.42L11.59 21.42A2 2 0 0 0 13 22A2 2 0 0 0 14.41 21.41L21.41 14.41A2 2 0 0 0 22 13A2 2 0 0 0 21.41 11.58M13 20L4 11V4H11L20 13M6.5 5A1.5 1.5 0 1 1 5 6.5A1.5 1.5 0 0 1 6.5 5Z'],
    [/tool|screwdriver|repair/, 'M18,1.83C17.5,1.83 17,2 16.59,2.41C13.72,5.28 8,11 8,11L9.5,12.5L6,16H4L2,20L4,22L8,20V18L11.5,14.5L13,16C13,16 18.72,10.28 21.59,7.41C22.21,6.5 22.37,5.37 21.59,4.59L19.41,2.41C19,2 18.5,1.83 18,1.83M18,4L20,6L13,13L11,11L18,4Z'],
    [/accessor|bag|pouch|grip/, 'M16,5V4A2,2 0 0,0 14,2H10A2,2 0 0,0 8,4V5A4,4 0 0,0 4,9V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V9A4,4 0 0,0 16,5M10,4H14V5H10V4M12,9L14,11L12,13L10,11L12,9M18,20H6V16H8V18H9V16H18V20M18,15H6V9A2,2 0 0,1 8,7H16A2,2 0 0,1 18,9V15Z'],
  ];
  const partIcon = (label) => {
    const name = norm(label);
    const match = ICONS.find(([pattern]) => pattern.test(name));
    return match ? `<svg class="home-promo__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${match[1]}"/></svg>` : '';
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  /* A drawn chevron for links that lead on (no typed arrows: docs/THEME-STYLE.md section 9) */
  const chevron = () => {
    const span = el('span', 'home-chevron');
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML = '<svg viewBox="0 0 8 12" width="8" height="12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 1.5 6.5 6 2 10.5"/></svg>';
    return span;
  };

  /* A photo that fades in over its frame once loaded, so a frame never jumps from empty (or an icon) to a photo */
  const photo = (frame, src, className = 'home-promo__image') => {
    if (!src) return;
    const image = el('img', `${className} is-loading`);
    image.alt = '';
    image.decoding = 'async';
    image.addEventListener('load', () => {
      requestAnimationFrame(() => image.classList.remove('is-loading'));
      frame.querySelectorAll('.home-promo__icon').forEach((icon) => icon.classList.add('is-covered'));
    }, { once: true });
    image.src = src;
    frame.append(image);
  };

  /* The panels' one tile (the same as the Build and Gamepads tiles, home-actions.css "Panel tiles"): a photo frame,
     the name and a quiet line under it. tag 'a' (with url) or 'button'. */
  const tile = ({ tag = 'a', url, title, line, image, icon }) => {
    const node = el(tag, 'home-promo');
    if (tag === 'a') node.href = url;
    else node.type = 'button';
    const media = el('span', 'home-promo__media');
    if (icon) media.innerHTML = icon;
    photo(media, image);
    const names = el('span', 'home-promo__names');
    names.append(el('span', 'home-promo__title heading', title), el('span', 'home-promo__edition', line || ''));
    const body = el('span', 'home-promo__body');
    body.append(names);
    node.append(media, body);
    return node;
  };

  const MS = { fast: 150, base: 250, slow: 350 };
  const RISE = 'translateY(8px)';
  const motionScale = () => (window.HHL && HHL.motion ? HHL.motion() : 1);
  const EASE = (window.HHL && HHL.ease) || 'cubic-bezier(.2, 0, 0, 1)';
  const still = () => HHL.mq.reducedMotion.matches;
  const shown = (node) => node && !node.hidden && node.nodeType === 1 && node.tagName !== 'TEMPLATE' && node.tagName !== 'DIALOG';

  class HomeActions extends HTMLElement {
    connectedCallback() {
      /* Set up once (the element can be moved, which connects it again) */
      if (this._ready) return;
      this._ready = true;

      this.cards = [...this.querySelectorAll('.home-actions__card[data-panel]')];
      this.panels = [...this.querySelectorAll('.home-panel')];
      this.grid = this.querySelector('.home-actions__grid');
      this.queue = Promise.resolve();
      this.queued = 0;
      this.running = new Set();

      this.cards.forEach((card) => card.addEventListener('click', (event) => {
        event.preventDefault();
        const name = card.dataset.panel;
        this.activePanel === name ? this.close() : this.open(name);
      }));

      this.querySelectorAll('[data-panel-back]').forEach((button) => button.addEventListener('click', () => {
        if (this.activePanel === 'upgrade' && this.funnel && this.funnel.step !== 'type') {
          this.goBack();
        } else if (this.openDetails[this.activePanel]) {
          this.showDetail(this.panelNamed(this.activePanel), null);
        } else {
          this.close();
        }
      }));

      this.setUpFunnel();
      this.setUpDetails();

      /* Theme editor: picking a block in the sidebar opens the panel (and larger view) it shows in */
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          const panel = this.contains(event.target) && event.target.closest('.home-panel');
          if (!panel) return;
          const name = panel.dataset.panelName;
          const detail = event.target.closest('[data-detail]')?.dataset.detail || null;
          if (this.activePanel !== name) this.open(name, detail);
          /* While a change is still running, the view it lands on isn't known yet: ask for this one after it */
          else if (this.queued || (this.openDetails[name] || null) !== detail) this.showDetail(panel, detail);
        });
      }
    }

    panelNamed(name) {
      return this.panels.find((panel) => panel.dataset.panelName === name);
    }

    /* ---------------- Motion (see the note at the top) ---------------- */

    /* One change at a time, in order; asking for another fast-forwards what is running */
    run(change) {
      this.queued++;
      this.running.forEach((animation) => animation.finish());
      const next = this.queue.then(change).catch(() => {}).finally(() => { this.queued--; });
      this.queue = next;
      return next;
    }

    animate(node, frames, options) {
      const fast = this.queued > 1 || still() || !node.animate;
      if (!node.animate) return null;
      const animation = node.animate(frames, { easing: EASE, ...options, duration: fast ? 0 : options.duration * motionScale(), delay: fast ? 0 : (options.delay || 0) * motionScale() });
      this.running.add(animation);
      animation.finished.catch(() => {}).finally(() => this.running.delete(animation));
      return animation;
    }

    /* What is leaving fades and sinks; resolves with the animations, which keep it hidden until cancelled */
    async leave(nodes) {
      const animations = nodes.filter(shown).map((node) => this.animate(node, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: RISE }], { duration: MS.fast, fill: 'forwards' })).filter(Boolean);
      await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
      return animations;
    }

    /* The room eases from the height it had to the one it has now; then what arrives rises into place */
    async arrive(room, from, nodes) {
      const to = room.getBoundingClientRect().height;
      const grows = Math.abs(to - from) > 1;
      const animations = [];
      if (grows) {
        room.style.overflow = 'clip';
        animations.push(this.animate(room, [{ height: `${from}px` }, { height: `${to}px` }], { duration: MS.slow }));
      }
      nodes.filter(shown).forEach((node) => animations.push(this.animate(node, [{ opacity: 0, transform: RISE }, { opacity: 1, transform: 'none' }], { duration: MS.base, delay: grows ? MS.slow * .35 : 0, fill: 'backwards' })));
      await Promise.all(animations.filter(Boolean).map((animation) => animation.finished.catch(() => {})));
      room.style.overflow = '';
    }

    /* A panel's room, open or shut: its height, padding, edge and the gap above it ease together */
    roomFrames(panel) {
      const style = getComputedStyle(panel);
      const open = { height: `${panel.getBoundingClientRect().height}px`, paddingTop: style.paddingTop, paddingBottom: style.paddingBottom, borderTopWidth: style.borderTopWidth, borderBottomWidth: style.borderBottomWidth, marginTop: style.marginTop, opacity: 1 };
      const shut = { height: '0px', paddingTop: '0px', paddingBottom: '0px', borderTopWidth: '0px', borderBottomWidth: '0px', marginTop: '0px', opacity: 0 };
      return { open, shut };
    }

    /* Swaps what a room holds: the old content leaves, change() runs, the room eases to fit and the new content arrives */
    async morph(room, change) {
      const from = room.getBoundingClientRect().height;
      const leaving = await this.leave([...room.children]);
      change();
      leaving.forEach((animation) => animation.cancel());
      await this.arrive(room, from, [...room.children]);
    }

    /* The cards become a row of tabs (or back): they fade, their row eases to its new height, they come back */
    async reshapeCards(open) {
      if (!this.grid || this.classList.contains('is-open') === open) return;
      const from = this.grid.getBoundingClientRect().height;
      const leaving = await this.leave([this.grid]);
      this.classList.toggle('is-open', open);
      /* The cards' own transitions (a phone tile's photo folding away) end now, while they're out of sight, so the row's
         new height is measured as it will settle */
      this.grid.getAnimations({ subtree: true }).forEach((animation) => { if (!leaving.includes(animation)) animation.finish(); });
      leaving.forEach((animation) => animation.cancel());
      await this.arrive(this.grid, from, [this.grid]);
    }

    markCards(name) {
      this.cards.forEach((card) => {
        const active = card.dataset.panel === name;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-expanded', active);
      });
    }

    /* What a panel shows when it opens: the first step, or its tiles (or the larger view asked for) */
    resetPanel(name, detail = null) {
      const panel = this.panelNamed(name);
      if (name === 'upgrade') {
        this.loadFunnel();
        this.drawStep('type');
      }
      if (panel?.querySelector('[data-detail-grid]')) this.applyDetail(panel, detail);
    }

    open(name, detail = null) {
      const previous = this.activePanel;
      this.activePanel = name;
      this.markCards(name);

      return this.run(async () => {
        const panel = this.panelNamed(name);
        const old = previous && previous !== name ? this.panelNamed(previous) : null;

        if (old && !old.hidden) {
          /* Another panel is open: its content leaves, the room takes the new panel's size, the new content arrives */
          const from = old.getBoundingClientRect().height;
          const leaving = await this.leave([...old.children]);
          old.hidden = true;
          leaving.forEach((animation) => animation.cancel());
          panel.hidden = false;
          this.resetPanel(name, detail);
          await this.arrive(panel, from, [...panel.children]);
          return;
        }

        this.scrollToCards();
        await this.reshapeCards(true);
        panel.hidden = false;
        this.resetPanel(name, detail);
        const { open, shut } = this.roomFrames(panel);
        panel.style.overflow = 'clip';
        const room = this.animate(panel, [shut, open], { duration: MS.slow });
        const content = [...panel.children].filter(shown).map((node) => this.animate(node, [{ opacity: 0, transform: RISE }, { opacity: 1, transform: 'none' }], { duration: MS.base, delay: MS.slow * .35, fill: 'backwards' }));
        await Promise.all([room, ...content].filter(Boolean).map((animation) => animation.finished.catch(() => {})));
        panel.style.overflow = '';
      });
    }

    close() {
      const name = this.activePanel;
      this.activePanel = null;
      this.markCards(null);

      return this.run(async () => {
        const panel = this.panelNamed(name);
        if (panel && !panel.hidden) {
          const leaving = await this.leave([...panel.children]);
          const { open, shut } = this.roomFrames(panel);
          panel.style.overflow = 'clip';
          const room = this.animate(panel, [open, shut], { duration: MS.slow, fill: 'forwards' });
          if (room) await room.finished.catch(() => {});
          panel.hidden = true;
          panel.style.overflow = '';
          room?.cancel();
          leaving.forEach((animation) => animation.cancel());
        }
        this.panels.forEach((item) => { item.hidden = true; });
        await this.reshapeCards(false);
      });
    }

    /* Brings the cards into view when they're out of it (or far down), so the panel opens where it can be seen */
    scrollToCards() {
      const top = this.getBoundingClientRect().top;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      if (top < header || top > window.innerHeight * .6) {
        window.scrollTo({ top: window.scrollY + top - header - 12, behavior: still() ? 'auto' : 'smooth' });
      }
    }

    /* ---------------- Larger views (builders, gamepads) ---------------- */

    setUpDetails() {
      this.openDetails = {};

      this.panels.filter((panel) => panel.querySelector('[data-detail-grid]')).forEach((panel) => {
        panel.querySelectorAll('[data-detail-open]').forEach((card) => card.addEventListener('click', (event) => {
          event.preventDefault();
          this.showDetail(panel, card.dataset.detailOpen);
        }));
      });

      const dialog = this.querySelector('[data-build-zoom-dialog]');
      if (!dialog) return;
      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-build-zoom]');
        if (!zoom) return;
        dialog.querySelector('[data-build-zoom-image]').src = zoom.dataset.buildZoom;
        dialog.querySelector('[data-build-zoom-caption]').textContent = zoom.dataset.buildCaption;
        dialog.showModal();
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    }

    /* A larger view in place of the panel's tiles (null: back to the tiles), as one change of the panel */
    showDetail(panel, id) {
      if (!panel?.querySelector('[data-detail-grid]')) return Promise.resolve();
      return this.run(() => this.morph(panel, () => this.applyDetail(panel, id)).then(() => {
        panel.querySelector('[data-detail]:not([hidden]) a, [data-detail]:not([hidden]) button')?.focus({ preventScroll: true });
      }));
    }

    applyDetail(panel, id) {
      const grid = panel.querySelector('[data-detail-grid]');
      if (!grid) return;
      this.openDetails[panel.dataset.panelName] = id;

      let shown = null;
      panel.querySelectorAll('[data-detail]').forEach((detail) => {
        const show = detail.dataset.detail === id;
        detail.hidden = !show;
        if (show) shown = detail;
      });

      const title = panel.querySelector('[data-panel-title]');
      const more = panel.querySelector('[data-panel-more]');
      grid.hidden = Boolean(shown);
      if (more) more.hidden = Boolean(shown);
      if (title) title.textContent = shown ? shown.dataset.detailTitle : title.dataset.defaultTitle;
      panel.querySelector('[data-back-label]').textContent = shown ? panel.dataset.detailBack : 'All options';
      panel.querySelectorAll('[data-detail-open]').forEach((card) => card.setAttribute('aria-expanded', card.dataset.detailOpen === id));
    }

    /* ---------------- Upgrade flow ---------------- */

    setUpFunnel() {
      if (!this.querySelector('[data-funnel-stage]')) return;
      this.stage = this.querySelector('[data-funnel-stage]');
      this.crumbs = this.querySelector('[data-funnel-crumbs]');
      this.backLabel = this.querySelector('.home-panel[data-panel-name="upgrade"] [data-back-label]');
      this.funnel = { step: 'type', type: null, console: null };
      this.groups = [];
      this.families = [];

      /* The menu data isn't in the page (it would add ~50 KB to every visit): it is fetched as the Upgrade card is
         pointed at, focused or pressed (sections/hhl-funnel-menu.liquid). A menu already in the page is used as is. */
      const inline = document.querySelector('[data-funnel-menu]');
      if (inline) this.useFunnelData(inline.textContent);
      const card = this.cards.find((item) => item.dataset.panel === 'upgrade');
      ['pointerenter', 'pointerdown', 'focusin'].forEach((type) => card?.addEventListener(type, () => this.loadFunnel(), { once: true, passive: true }));
    }

    /* The menu data, once: resolves when it's ready (or failed: the first step then shows without counts) */
    loadFunnel() {
      if (this.groupsReady) return Promise.resolve();
      if (!this.funnelLoad) {
        this.funnelLoad = fetch(this.dataset.funnelUrl, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((html) => {
            const data = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-funnel-menu]');
            this.useFunnelData(data ? data.textContent : '[]');
            if (this.activePanel === 'upgrade' && this.funnel.step === 'type') this.updateTypeCounts();
          })
          .catch(() => { this.funnelLoad = null; });
      }
      return this.funnelLoad;
    }

    useFunnelData(text) {
      let groups = [];
      try { groups = JSON.parse(text || '[]'); } catch (error) { groups = []; }
      this.groupsReady = true;

      const handheldWords = splitWords(this.dataset.handheldKeywords);
      const bothWords = splitWords(this.dataset.bothKeywords);
      const excluded = splitWords(this.dataset.excludeGroups);

      /* Builders anywhere in the menu (a link with "builder" in its name), e.g. "Game Boy Color Ultimate Builder" */
      const menuBuilders = [];
      groups.forEach((group) => group.consoles.forEach((item) => {
        [item, ...item.links].forEach((link) => {
          if (/builder/i.test(link.title) && link.url && !menuBuilders.some((b) => b.url === link.url)) menuBuilders.push({ title: link.title, url: link.url });
        });
      }));
      /* Builder promos in this section's Build panel open its larger view in place */
      const promoBuilders = Array.from(this.querySelectorAll('.home-panel[data-panel-name="builders"] [data-detail-grid] .home-promo')).map((card) => ({
        title: card.querySelector('.home-promo__title')?.textContent || '',
        url: card.getAttribute('href'),
        detail: card.dataset.detailOpen || null,
        label: card.querySelector('.home-promo__button')?.textContent.trim() || '',
        image: card.querySelector('.home-promo__image')?.getAttribute('src') || ''
      }));

      /* Each console gets its kinds: handheld, home or both */
      this.groups = groups.filter((group) => !excluded.includes(group.title.toLowerCase().trim())).map((group) => {
        const groupName = group.title.toLowerCase();
        return {
          ...group,
          consoles: group.consoles.map((item) => {
            let kinds;
            if (matchesAny(item.title, bothWords)) kinds = ['handheld', 'home'];
            else if (groupName.includes('handheld')) kinds = ['handheld'];
            else if (groupName.includes('console')) kinds = ['home'];
            else kinds = matchesAny(item.title, handheldWords) ? ['handheld'] : ['home'];
            return { ...item, color: group.color, kinds, prefix: sharedPrefix(item.links), builders: [] };
          })
        };
      });

      /* Console families ("Game Boy Advance: Game Boy Advance, Game Boy Micro", one per line) split the menu groups
         into finer ones on the console step; the longest matching word wins */
      this.families = (this.dataset.consoleFamilies || '').split(/\r?\n/).map((line) => {
        const [title, words] = line.split(':');
        return title && words ? { title: title.trim(), words: splitWords(words) } : null;
      }).filter(Boolean);

      this.groups.forEach((group) => group.consoles.forEach((item) => {
        const name = item.title.toLowerCase();
        let length = 0;
        this.families.forEach((family) => family.words.forEach((word) => {
          if (word && name.includes(word) && word.length > length) {
            item.family = family.title;
            length = word.length;
          }
        }));
      }));

      /* Each builder goes to the console whose name (or the short name its parts share) it contains,
         the longest match winning, so "Game Boy Advance SP Builder" goes to the SP, not the GBA. */
      const consoles = this.groups.flatMap((group) => group.consoles);
      [...promoBuilders, ...menuBuilders].forEach((builder) => {
        const name = norm(builder.title);
        let best = null, bestLength = 0;
        consoles.forEach((item) => {
          [norm(item.title), item.prefix ? norm(item.prefix) : ''].forEach((key) => {
            if (key.trim().length > 2 && name.includes(key) && key.length > bestLength) {
              best = item;
              bestLength = key.length;
            }
          });
        });
        if (best && !best.builders.some((b) => b.url === builder.url || norm(b.title) === name)) best.builders.push(builder);
      });
    }

    consolesOfKind(kind) {
      /* Families first (in their listed order), then each menu group with what is left; a console listed in two
         menu groups (Switch under handhelds and consoles) shows once */
      const seen = new Set();
      const fresh = (item) => {
        const key = item.url || item.title;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      };
      const ofKind = this.groups.map((group) => ({ ...group, consoles: group.consoles.filter((item) => item.kinds.includes(kind)) }));

      const families = this.families.map((family) => {
        const consoles = ofKind.flatMap((group) => group.consoles.filter((item) => item.family === family.title)).filter(fresh);
        const color = ofKind.find((group) => group.consoles.some((item) => item.family === family.title))?.color;
        return { title: family.title, color, consoles };
      });
      const rest = ofKind.map((group) => ({ ...group, consoles: group.consoles.filter((item) => !item.family).filter(fresh) }));

      return [...families, ...rest].filter((group) => group.consoles.length > 0);
    }

    goBack() {
      this.showStep(this.funnel.step === 'parts' ? 'console' : 'type');
    }

    /* A step of the Upgrade flow, as one change of its panel (the old step leaves, the room fits the new one) */
    showStep(step) {
      const panel = this.panelNamed('upgrade');
      return this.run(() => this.morph(panel, () => this.drawStep(step)).then(() => {
        this.stage.querySelector('a, button:not(:disabled)')?.focus({ preventScroll: true });
      }));
    }

    drawStep(step) {
      this.funnel.step = step;
      this.stage.replaceChildren();
      if (step === 'type') this.drawTypes();
      if (step === 'console') this.drawConsoles();
      if (step === 'parts') this.drawParts();

      this.drawCrumbs();
      this.backLabel.textContent = step === 'type' ? 'All options' : 'Back';
    }

    drawCrumbs() {
      const { step, type, console: item } = this.funnel;
      const trail = [{ label: 'Upgrade', step: 'type' }];
      if (step !== 'type') trail.push({ label: type === 'handheld' ? 'Handheld' : 'Home console', step: 'console' });
      if (step === 'parts') trail.push({ label: item.title, step: 'parts' });

      this.crumbs.replaceChildren(...trail.flatMap((crumb, index) => {
        const last = index === trail.length - 1;
        const node = last ? el('span', 'home-panel__crumb is-current', crumb.label) : el('button', 'home-panel__crumb', crumb.label);
        if (!last) {
          node.type = 'button';
          node.addEventListener('click', () => this.showStep(crumb.step));
        } else {
          node.setAttribute('aria-current', 'step');
        }
        return index === 0 ? [node] : [el('span', 'home-panel__crumb-sep', '/'), node];
      }));
    }

    drawTypes() {
      const grid = el('div', 'home-funnel__choices');
      const copy = this.dataset;

      [['handheld', copy.typeHandheld || 'Handheld', copy.typeHandheldHint || 'Game Boy, GBA, DS, PSP and more'], ['home', copy.typeHome || 'Home console', copy.typeHomeHint || 'GameCube, N64, PlayStation, Xbox and more']].forEach(([kind, label, hint]) => {
        const button = tile({ tag: 'button', title: label, line: hint });
        button.classList.add('home-funnel__choice');
        button.dataset.kind = kind;
        button.dataset.hint = hint;
        button.addEventListener('click', () => {
          this.funnel.type = kind;
          /* The menu data may still be on its way (a quick tap): the next step follows as soon as it's here */
          this.loadFunnel().then(() => {
            if (this.activePanel === 'upgrade' && this.funnel.step === 'type' && this.funnel.type === kind) this.showStep('console');
          });
        });
        grid.append(button);
      });

      this.stage.append(el('p', 'home-funnel__question heading', copy.typeQuestion || 'What are you upgrading?'), grid);
      this.updateTypeCounts();
    }

    /* The console count on each first-step choice, once the menu data is here */
    updateTypeCounts() {
      if (!this.groupsReady) return;
      this.stage.querySelectorAll('.home-funnel__choice[data-kind]').forEach((button) => {
        const consoles = this.consolesOfKind(button.dataset.kind).reduce((count, group) => count + group.consoles.length, 0);
        const hint = button.querySelector('.home-promo__edition');
        const text = consoles ? `${consoles} consoles · ${button.dataset.hint}` : button.dataset.hint;
        if (hint.textContent !== text) hint.textContent = text;
        button.disabled = consoles === 0;
        /* A real console of that kind in the frame: the first one with a photo */
        const media = button.querySelector('.home-promo__media');
        if (!media.querySelector('img')) {
          const first = this.consolesOfKind(button.dataset.kind).flatMap((group) => group.consoles).find((item) => item.image);
          if (first) photo(media, first.image);
        }
      });
    }

    drawConsoles() {
      const wrapper = el('div', 'home-funnel__brands');

      this.consolesOfKind(this.funnel.type).forEach((group) => {
        const brand = el('div', 'home-funnel__brand');
        const list = el('div', 'home-funnel__tiles');

        group.consoles.forEach((item) => {
          /* A console with no part links goes straight to its page */
          const direct = item.links.length === 0 && item.url;
          const row = el(direct ? 'a' : 'button', 'home-funnel__tile');
          if (direct) row.href = item.url;
          else {
            row.type = 'button';
            row.addEventListener('click', () => {
              this.funnel.console = item;
              this.showStep('parts');
            });
          }
          const thumb = el('span', 'home-funnel__tile-thumb');
          photo(thumb, item.image, 'home-funnel__tile-image');
          row.append(thumb, el('span', 'home-funnel__tile-title', item.title), chevron());
          list.append(row);
        });

        brand.append(el('p', 'home-funnel__brand-name', group.title), list);
        wrapper.append(brand);
      });

      this.stage.append(el('p', 'home-funnel__question heading', this.dataset.consoleQuestion || 'Which console?'), wrapper);
    }

    drawParts() {
      const item = this.funnel.console;
      const wrapper = el('div', 'home-funnel__parts');

      if (item.builders.length) wrapper.append(this.builderBanner(item));

      const grid = el('div', 'home-tiles home-funnel__cards');
      const shopAll = item.links.find((link) => isShopAll(link.title));
      let allUrl = item.url || shopAll?.url;
      /* A console collection with part types has an explore page (sections/main-collection-explore.liquid, the same
         rule as the sidebar's "Explore" links): "Shop all" lands there */
      if (item.url && item.links.length && /^\/collections\/[^/?#]+$/.test(new URL(item.url, location.origin).pathname)) {
        allUrl = `${item.url}${item.url.includes('?') ? '&' : '?'}view=explore`;
      }
      if (allUrl) {
        grid.append(this.partCard({ title: `${this.dataset.shopAllText || 'Shop all'} ${item.title}`, url: allUrl, image: item.image || shopAll?.image, count: item.count ?? shopAll?.count }, true));
      }
      item.links.filter((link) => !isShopAll(link.title)).forEach((link) => {
        grid.append(this.partCard({ ...link, title: partLabel(link.title, item.prefix) }));
      });
      wrapper.append(grid);

      this.stage.append(el('p', 'home-funnel__question heading', (this.dataset.partsQuestion || 'What does your [console] need?').replace('[console]', item.title)), wrapper);
    }

    partCard(part, featured = false) {
      const card = tile({ url: part.url, title: part.title, line: part.count ? `${part.count} product${part.count === 1 ? '' : 's'}` : '', icon: featured ? '' : partIcon(part.title) });
      const media = card.querySelector('.home-promo__media');
      /* A collection's own image is often one banner shared by every category, so tiles show the
         collection's first product photo, looked up when the console is picked; the collection
         image is the fallback. */
      const collection = part.type === 'collection' && /^\/collections\/[^/?#]+/.exec(new URL(part.url, location.origin).pathname);
      if (collection && !featured) {
        fetch(`${collection[0]}/products.json?limit=1`)
          .then((response) => response.ok ? response.json() : null)
          .then((data) => {
            const src = data?.products?.[0]?.images?.[0]?.src;
            photo(media, src ? `${src}${src.includes('?') ? '&' : '?'}width=480` : part.image);
          })
          .catch(() => photo(media, part.image));
      } else {
        photo(media, part.image);
      }
      return card;
    }

    /* "Want it built for you?": the console's builders. A builder with a promo in the Build panel
       opens its larger view there; otherwise the link goes straight to the builder. */
    builderBanner(item) {
      const [main, ...others] = item.builders;
      const banner = el(main.detail ? 'button' : 'a', 'home-start home-start--compact');
      if (main.detail) {
        banner.type = 'button';
        banner.addEventListener('click', () => this.open('builders', main.detail));
      } else {
        banner.href = main.url;
      }

      const media = el('span', 'home-start__media');
      photo(media, main.image || item.image, 'home-start__image');
      const body = el('span', 'home-start__body');
      body.append(
        el('span', 'home-start__title heading', this.dataset.builderTitle || 'Want it built for you?'),
        el('span', 'home-start__text', (this.dataset.builderText || 'Design a custom [console] in our builder. We assemble and test it, or send you the kit.').replace('[console]', item.title))
      );
      /* Other builders of the console that the first one's view doesn't already list */
      if (others.length && !main.detail) {
        const more = el('span', 'home-start__text');
        others.forEach((builder, index) => {
          const link = el('a', '', builder.title);
          link.href = builder.url;
          link.addEventListener('click', (event) => event.stopPropagation());
          more.append(index ? ' · ' : 'Also: ', link);
        });
        body.append(more);
      }
      body.append(el('span', 'home-promo__button home-start__button', main.detail ? (main.label || 'Explore builds') : (this.dataset.builderButton || 'Start building')));
      banner.append(media, body);
      return banner;
    }
  }

  customElements.define('home-actions', HomeActions);
})();
